import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../services/api";

export default function AdminLogin() {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPwd,  setShowPwd]  = useState(false);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Session-based: check sessionStorage (clears when browser tab closes)
    if (sessionStorage.getItem("adminToken")) navigate("/admin", { replace: true });
  }, [navigate]);

  const login = async () => {
    if (!email || !password) { setError("Enter email and password"); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setError("Enter a valid email address"); return; }
    try {
      setLoading(true);
      setError("");
      const res = await api.post("/admin/login", { email, password });
      // Use sessionStorage — clears automatically when tab/browser closes
      sessionStorage.setItem("adminToken", res.data.token);
      sessionStorage.setItem("adminUser",  JSON.stringify(res.data.admin));
      navigate("/admin", { replace: true });
    } catch {
      setError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") login(); };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 p-4">

      {/* Back to website button */}
      <div className="w-full max-w-sm mb-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-900 font-medium transition"
        >
          ← Back to Website
        </Link>
      </div>

      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-3 text-white text-2xl font-bold">
            🏥
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Admin Portal</h1>
          <p className="text-sm text-gray-400 mt-1">Clinic Management System</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-2 mb-4 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
              placeholder="admin@clinic.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={onKey}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPwd ? "text" : "password"}
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={onKey}
              />
              <button
                type="button"
                onClick={() => setShowPwd(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition text-lg"
                tabIndex={-1}
                aria-label={showPwd ? "Hide password" : "Show password"}
              >
                {showPwd ? "🙈" : "👁️"}
              </button>
            </div>
          </div>
          <div className="text-right">
            <Link to="/clinic-management/forgot-password" className="text-xs text-green-700 hover:text-green-900 font-medium transition">
              Forgot password?
            </Link>
          </div>
        </div>

        <button
          onClick={login}
          disabled={loading}
          className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition disabled:opacity-60 text-sm"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>

        <p className="text-center text-xs text-gray-400 mt-4">
          Session expires when you close this tab.
        </p>
      </div>
    </div>
  );
}
